import { useCallback, useMemo, useState, useEffect } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import { isValidProjectId } from '../../utils/projectIds';
import './StoryBiblePage.css';

/* ── Constants ── */
const getCategories = (t) => [
  { key: 'all', label: t('storyBiblePage.categories.all'), icon: '◉' },
  { key: 'character', label: t('storyBiblePage.categories.character'), icon: '👤' },
  { key: 'location', label: t('storyBiblePage.categories.location'), icon: '📍' },
  { key: 'lore', label: t('storyBiblePage.categories.lore'), icon: '📜' },
  { key: 'item', label: t('storyBiblePage.categories.item'), icon: '🔮' },
  { key: 'subplot', label: t('storyBiblePage.categories.subplot'), icon: '⚡' },
  { key: 'custom', label: t('storyBiblePage.categories.custom'), icon: '📎' },
];

const getLegacySectionLabels = (t) => ({
  summary: t('storyBiblePage.legacySections.summary'),
  characters: t('storyBiblePage.legacySections.characters'),
  relationships: t('storyBiblePage.legacySections.relationships'),
  plot_points: t('storyBiblePage.legacySections.plotPoints'),
  timeline: t('storyBiblePage.legacySections.timeline'),
  world_rules: t('storyBiblePage.legacySections.worldRules'),
  open_threads: t('storyBiblePage.legacySections.openThreads'),
  revision_risks: t('storyBiblePage.legacySections.revisionRisks'),
});

const CATEGORY_COLORS = {
  character: '#d4a26a',
  location: '#6abed4',
  lore: '#b89adb',
  item: '#d4c36a',
  subplot: '#e07a5f',
  custom: '#888',
};

/* ═══════ Component ═══════ */
function StoryBiblePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const safeProjectId = isValidProjectId(projectId) ? projectId : null;
  const categories = useMemo(() => getCategories(t), [t]);
  const backArrow = i18n.dir() === 'rtl' ? '→' : '←';

  const project = useQuery(api.projects.getById, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const entries = useQuery(api.storyBibleEntries.list, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const legacyViewModel = useQuery(api.storyBibles.getViewModel, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const continuityRecords = useQuery(api.fileContinuity.listByProject, safeProjectId ? { projectId: safeProjectId } : 'skip');

  const createEntry = useMutation(api.storyBibleEntries.create);
  const updateEntry = useMutation(api.storyBibleEntries.update);
  const removeEntry = useMutation(api.storyBibleEntries.remove);
  const addRelationship = useMutation(api.storyBibleEntries.addRelationship);
  const removeRelationship = useMutation(api.storyBibleEntries.removeRelationship);
  const generateAction = useAction(api.storyBibleEntries.generateFromManuscript);
  const migrateAction = useAction(api.storyBibleEntries.migrateFromLegacy);

  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const selectedEntry = useQuery(
    api.storyBibleEntries.getById,
    selectedId ? { entryId: selectedId } : 'skip',
  );

  // Clear message after 5s
  useEffect(() => {
    if (!message.text) return;
    const t = setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    return () => clearTimeout(t);
  }, [message.text]);

  const filteredEntries = useMemo(() => {
    if (!entries) return [];
    let list = entries;
    if (activeCategory !== 'all') list = list.filter((e) => e.category === activeCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((e) =>
        e.name.toLowerCase().includes(q) ||
        e.aliases?.some((a) => a.toLowerCase().includes(q)) ||
        e.description?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [entries, activeCategory, searchQuery]);

  const categoryCounts = useMemo(() => {
    if (!entries) return {};
    const c = { all: entries.length };
    for (const e of entries) c[e.category] = (c[e.category] || 0) + 1;
    return c;
  }, [entries]);

  const recentCanonUpdates = useMemo(() => {
    if (!continuityRecords) return [];
    return [...continuityRecords]
      .sort((a, b) => (b.lastProcessedAt || b.updatedAt || 0) - (a.lastProcessedAt || a.updatedAt || 0))
      .flatMap((record) =>
        (record.canonUpdates || []).map((update) => ({
          filePath: record.filePath,
          update,
        }))
      )
      .slice(0, 8);
  }, [continuityRecords]);

  // AI Generate
  const handleGenerate = async () => {
    if (!safeProjectId || isGenerating) return;
    setIsGenerating(true);
    setMessage({ text: '', type: '' });
    try {
      const result = await generateAction({ projectId: safeProjectId });
      setMessage({ text: t('storyBiblePage.generatedEntries', { count: result.count }), type: 'success' });
    } catch (err) {
      setMessage({ text: err?.data?.message || err?.message || t('storyBiblePage.generationFailed'), type: 'error' });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!confirm(t('storyBiblePage.confirmDelete'))) return;
    await removeEntry({ entryId });
    if (selectedId === entryId) setSelectedId(null);
  };

  /* Guards */
  if (!safeProjectId) {
    return <main className="sb-page sb-loading"><div className="sb-empty-card">{t('common.invalidProjectLink')} <button className="btn btn-secondary" onClick={() => navigate('/projects')}>{t('common.back')}</button></div></main>;
  }
  if (project === undefined || entries === undefined) {
    return <main className="sb-page sb-loading" aria-busy="true"><div className="sb-loader"><div className="sb-loader-ring" /><span>{t('storyBiblePage.loading')}</span></div></main>;
  }
  if (!project) {
    return <main className="sb-page sb-loading"><div className="sb-empty-card">{t('common.projectNotFound')} <button className="btn btn-secondary" onClick={() => navigate('/projects')}>{t('common.back')}</button></div></main>;
  }

  return (
    <main className="sb-page">
      {/* ── Header ── */}
      <header className="sb-header">
        <div className="sb-header-left">
          <div className="sb-breadcrumb">
            <button className="sb-breadcrumb-link" onClick={() => navigate(`/projects/${safeProjectId}`)}>{backArrow} {project.title}</button>
          </div>
          <h1 className="sb-title">{t('storyBiblePage.title')}</h1>
          <p className="sb-subtitle">{t('storyBiblePage.subtitle')}</p>
        </div>
        <div className="sb-header-actions">
          <button
            className="sb-generate-btn"
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <><span className="sb-gen-spinner" /> {t('storyBiblePage.analyzing')}</>
            ) : (
              <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg> {t('storyBiblePage.generateWithAi')}</>
            )}
          </button>
          <button className="sb-add-btn" onClick={() => { setShowCreate(true); setSelectedId(null); }}>
            + {t('storyBiblePage.addEntry')}
          </button>
        </div>
      </header>

      {message.text && <div className={`sb-alert ${message.type}`}>{message.text}</div>}

      {recentCanonUpdates.length > 0 && (
        <section className="sb-updates-panel">
          <div className="sb-updates-header">
            <h2>{t('storyBiblePage.recentUpdatesTitle')}</h2>
            <p>{t('storyBiblePage.recentUpdatesDescription')}</p>
          </div>
          <div className="sb-updates-list">
            {recentCanonUpdates.map((item, index) => (
              <div key={`${item.filePath}-${index}`} className="sb-update-item">
                <div className="sb-update-path">{item.filePath}</div>
                <div className="sb-update-text">{item.update}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Empty state or legacy fallback ── */}
      {entries.length === 0 && !showCreate ? (
        legacyViewModel ? (
          /* Show legacy story bible with upgrade banner */
          <div>
            <div className="sb-upgrade-banner">
              <div className="sb-upgrade-text">
                <strong>{t('storyBiblePage.upgradeTitle')}</strong> — {t('storyBiblePage.upgradeDescription')}
              </div>
              <div className="sb-upgrade-actions">
                <button className="sb-generate-btn" onClick={handleGenerate} disabled={isGenerating}>
                  {isGenerating ? <><span className="sb-gen-spinner" /> {t('storyBiblePage.upgrading')}</> : t('storyBiblePage.upgradeWithAi')}
                </button>
                <button className="sb-add-btn" onClick={() => { setShowCreate(true); }}>+ {t('storyBiblePage.addEntry')}</button>
              </div>
            </div>
            <LegacyView viewModel={legacyViewModel} />
          </div>
        ) : (
          /* Truly empty — no data at all */
          <section className="sb-empty">
            <div className="sb-empty-visual">
              <div className="sb-empty-orb" />
            </div>
            <h2>{t('storyBiblePage.emptyTitle')}</h2>
            <p>{t('storyBiblePage.emptyDescription')}</p>
            <div className="sb-empty-actions">
              <button className="sb-generate-btn" onClick={handleGenerate} disabled={isGenerating}>
                {isGenerating ? <><span className="sb-gen-spinner" /> {t('storyBiblePage.generating')}</> : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg> {t('storyBiblePage.generateFromManuscript')}</>}
              </button>
              <button className="btn btn-secondary" onClick={() => setShowCreate(true)}>{t('storyBiblePage.addManually')}</button>
            </div>
          </section>
        )
      ) : (
        <div className={`sb-layout ${selectedEntry ? 'has-detail' : ''}`}>
          {/* ── Sidebar ── */}
          <aside className="sb-sidebar">
            <div className="sb-search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input type="text" placeholder={t('storyBiblePage.searchPlaceholder')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              {searchQuery && <button className="sb-search-clear" onClick={() => setSearchQuery('')}>×</button>}
            </div>
            <nav className="sb-categories">
              {categories.map((cat) => {
                const count = categoryCounts[cat.key] || 0;
                if (cat.key !== 'all' && count === 0) return null;
                return (
                  <button key={cat.key} className={`sb-cat-btn ${activeCategory === cat.key ? 'active' : ''}`} onClick={() => setActiveCategory(cat.key)}>
                    <span className="sb-cat-icon">{cat.icon}</span>
                    <span className="sb-cat-label">{cat.label}</span>
                    {count > 0 && <span className="sb-cat-count">{count}</span>}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* ── Main ── */}
          <section className="sb-content">
            {showCreate && (
              <CreateEntryForm
                projectId={safeProjectId}
                onCreate={createEntry}
                onClose={() => setShowCreate(false)}
                defaultCategory={activeCategory !== 'all' ? activeCategory : 'character'}
              />
            )}
            <div className="sb-grid">
              {filteredEntries.map((entry) => (
                <EntryCard
                  key={entry._id}
                  entry={entry}
                  isSelected={selectedId === entry._id}
                  onClick={() => setSelectedId(selectedId === entry._id ? null : entry._id)}
                  onDelete={() => handleDelete(entry._id)}
                />
              ))}
              {filteredEntries.length === 0 && entries.length > 0 && (
                <p className="sb-no-results">{t('storyBiblePage.noResults', { query: searchQuery })}</p>
              )}
            </div>
          </section>

          {/* ── Detail ── */}
          {selectedEntry && (
            <EntryDetail
              entry={selectedEntry}
              allEntries={entries}
              onUpdate={updateEntry}
              onClose={() => setSelectedId(null)}
              onAddRelationship={addRelationship}
              onRemoveRelationship={removeRelationship}
              onSelectEntry={setSelectedId}
              projectId={safeProjectId}
            />
          )}
        </div>
      )}
    </main>
  );
}

/* ═══════ Entry Card ═══════ */
function EntryCard({ entry, isSelected, onClick, onDelete }) {
  const { t } = useTranslation();
  const categories = useMemo(() => getCategories(t), [t]);
  const catColor = CATEGORY_COLORS[entry.category] || '#888';
  const catInfo = categories.find((c) => c.key === entry.category);

  return (
    <button className={`sb-card ${isSelected ? 'selected' : ''}`} onClick={onClick}>
      <div className="sb-card-top">
        <span className="sb-card-cat" style={{ '--cat-color': catColor }}>
          {catInfo?.icon}
        </span>
        <button className="sb-card-x" onClick={(e) => { e.stopPropagation(); onDelete(); }} title={t('storyBiblePage.deleteEntry')}>×</button>
      </div>
      <h3 className="sb-card-name">{entry.name}</h3>
      {entry.aliases?.length > 0 && <p className="sb-card-aliases">{entry.aliases.join(' · ')}</p>}
      {entry.description && <p className="sb-card-desc">{entry.description.slice(0, 100)}{entry.description.length > 100 ? '…' : ''}</p>}
      {entry.quickFacts?.length > 0 && (
        <div className="sb-card-facts">
          {entry.quickFacts.slice(0, 2).map((f, i) => (
            <span key={i} className="sb-card-fact"><strong>{f.key}</strong> {f.value}</span>
          ))}
        </div>
      )}
    </button>
  );
}

/* ═══════ Entry Detail ═══════ */
function EntryDetail({ entry, allEntries, onUpdate, onClose, onAddRelationship, onRemoveRelationship, onSelectEntry, projectId }) {
  const { t } = useTranslation();
  const categories = useMemo(() => getCategories(t), [t]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [showAddRel, setShowAddRel] = useState(false);
  const [relTarget, setRelTarget] = useState('');
  const [relLabel, setRelLabel] = useState('');

  // Reset on entry change
  useEffect(() => {
    setForm({
      name: entry.name,
      aliases: (entry.aliases || []).join(', '),
      description: entry.description || '',
      quickFacts: entry.quickFacts?.length > 0 ? [...entry.quickFacts] : [{ key: '', value: '' }],
      tags: (entry.tags || []).join(', '),
    });
    setEditing(false);
    setShowAddRel(false);
  }, [entry._id]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFact = (i, field, val) => {
    const facts = [...form.quickFacts];
    facts[i] = { ...facts[i], [field]: val };
    setField('quickFacts', facts);
  };

  const handleSave = async () => {
    await onUpdate({
      entryId: entry._id,
      name: form.name,
      aliases: form.aliases.split(',').map((a) => a.trim()).filter(Boolean),
      description: form.description,
      quickFacts: form.quickFacts.filter((f) => f.key.trim() || f.value.trim()),
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
    });
    setEditing(false);
  };

  const handleAddRel = async () => {
    if (!relTarget || !relLabel.trim()) return;
    await onAddRelationship({ projectId, sourceEntryId: entry._id, targetEntryId: relTarget, label: relLabel.trim(), bidirectional: true });
    setShowAddRel(false);
    setRelTarget('');
    setRelLabel('');
  };

  const catColor = CATEGORY_COLORS[entry.category] || '#888';
  const catInfo = categories.find((c) => c.key === entry.category);

  return (
    <aside className="sb-detail">
      {/* Header */}
      <div className="sb-detail-top">
        <span className="sb-detail-badge" style={{ '--cat-color': catColor }}>{catInfo?.icon} {catInfo?.label}</span>
        <div className="sb-detail-btns">
          {editing ? (
            <>
              <button className="sb-btn-sm sb-btn-primary" onClick={handleSave}>{t('common.save')}</button>
              <button className="sb-btn-sm" onClick={() => setEditing(false)}>{t('common.cancel')}</button>
            </>
          ) : (
            <button className="sb-btn-sm" onClick={() => setEditing(true)}>{t('common.edit')}</button>
          )}
          <button className="sb-close-btn" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      {editing ? (
        /* ── Edit mode ── */
        <div className="sb-edit">
          <input className="sb-edit-name" value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder={t('storyBiblePage.fields.name')} />
          <input className="sb-edit-input" value={form.aliases} onChange={(e) => setField('aliases', e.target.value)} placeholder={t('storyBiblePage.fields.aliases')} />
          <textarea className="sb-edit-textarea" value={form.description} onChange={(e) => setField('description', e.target.value)} rows={5} placeholder={t('storyBiblePage.fields.description')} />

          <div className="sb-edit-section-label">{t('storyBiblePage.quickFacts')}</div>
          {form.quickFacts.map((f, i) => (
            <div key={i} className="sb-edit-fact-row">
              <input placeholder={t('storyBiblePage.fields.key')} value={f.key} onChange={(e) => setFact(i, 'key', e.target.value)} />
              <input placeholder={t('storyBiblePage.fields.value')} value={f.value} onChange={(e) => setFact(i, 'value', e.target.value)} />
              <button className="sb-edit-fact-x" onClick={() => setField('quickFacts', form.quickFacts.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
          <button className="sb-btn-sm" onClick={() => setField('quickFacts', [...form.quickFacts, { key: '', value: '' }])}>+ {t('storyBiblePage.fact')}</button>

          <div className="sb-edit-section-label">{t('storyBiblePage.tags')}</div>
          <input className="sb-edit-input" value={form.tags} onChange={(e) => setField('tags', e.target.value)} placeholder={t('storyBiblePage.fields.tagsPlaceholder')} />
        </div>
      ) : (
        /* ── View mode ── */
        <div className="sb-view">
          <h2 className="sb-view-name">{entry.name}</h2>
          {entry.aliases?.length > 0 && <p className="sb-view-aliases">{entry.aliases.join(' · ')}</p>}
          {entry.description && <p className="sb-view-desc">{entry.description}</p>}

          {entry.quickFacts?.length > 0 && (
            <div className="sb-view-facts">
              {entry.quickFacts.map((f, i) => (
                <div key={i} className="sb-view-fact">
                  <span className="sb-view-fact-key">{f.key}</span>
                  <span className="sb-view-fact-val">{f.value}</span>
                </div>
              ))}
            </div>
          )}

          {entry.tags?.length > 0 && (
            <div className="sb-view-tags">
              {entry.tags.map((tag, i) => <span key={i} className="sb-view-tag">{tag}</span>)}
            </div>
          )}
        </div>
      )}

      {/* ── Relationships ── */}
      <div className="sb-rels">
        <div className="sb-rels-top">
          <span className="sb-rels-title">{t('storyBiblePage.connections')}</span>
          <button className="sb-btn-sm" onClick={() => setShowAddRel(!showAddRel)}>+ {t('storyBiblePage.link')}</button>
        </div>

        {showAddRel && (
          <div className="sb-rels-form">
            <select value={relTarget} onChange={(e) => setRelTarget(e.target.value)}>
              <option value="">{t('storyBiblePage.chooseEntry')}</option>
              {(allEntries || []).filter((e) => e._id !== entry._id).map((e) => (
                <option key={e._id} value={e._id}>{categories.find((c) => c.key === e.category)?.icon} {e.name}</option>
              ))}
            </select>
            <input placeholder={t('storyBiblePage.relationshipLabel')} value={relLabel} onChange={(e) => setRelLabel(e.target.value)} />
            <button className="sb-btn-sm sb-btn-primary" onClick={handleAddRel} disabled={!relTarget || !relLabel.trim()}>{t('common.add')}</button>
          </div>
        )}

        {entry.relationships?.length > 0 ? (
          <div className="sb-rels-list">
            {entry.relationships.map((rel) => (
              <div key={rel._id} className="sb-rel-row">
                <button className="sb-rel-name" onClick={() => onSelectEntry(rel.relatedId)}>{rel.relatedName}</button>
                <span className="sb-rel-label">{rel.label}</span>
                <button className="sb-rel-x" onClick={() => onRemoveRelationship({ relationshipId: rel._id })}>×</button>
              </div>
            ))}
          </div>
        ) : (
          <p className="sb-rels-none">{t('storyBiblePage.noConnections')}</p>
        )}
      </div>
    </aside>
  );
}

/* ═══════ Create Entry Form — AI-first with manual toggle ═══════ */
function CreateEntryForm({ projectId, onCreate, onClose, defaultCategory }) {
  const { t } = useTranslation();
  const categories = useMemo(() => getCategories(t), [t]);
  const [mode, setMode] = useState('ai'); // 'ai' | 'manual'
  const [freeText, setFreeText] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState(defaultCategory);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const createFromFreeform = useAction(api.storyBibleEntries.createFromFreeform);

  const handleAiSubmit = async (e) => {
    e.preventDefault();
    if (!freeText.trim() || saving) return;
    setSaving(true);
    try {
      await createFromFreeform({ projectId, text: freeText.trim() });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || saving) return;
    setSaving(true);
    try {
      await onCreate({ projectId, category, name: name.trim(), description: description.trim() || undefined });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sb-create">
      {/* Mode toggle */}
      <div className="sb-create-top">
        <h3>{t('storyBiblePage.newEntry')}</h3>
        <div className="sb-mode-toggle">
          <button className={`sb-mode-btn ${mode === 'ai' ? 'active' : ''}`} onClick={() => setMode('ai')}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/></svg>
            {t('storyBiblePage.aiAssist')}
          </button>
          <button className={`sb-mode-btn ${mode === 'manual' ? 'active' : ''}`} onClick={() => setMode('manual')}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
            {t('storyBiblePage.manual')}
          </button>
        </div>
      </div>

      {mode === 'ai' ? (
        /* ── AI mode: just type, AI organizes ── */
        <form onSubmit={handleAiSubmit}>
          <p className="sb-create-hint">{t('storyBiblePage.aiHint')}</p>
          <textarea
            className="sb-freeform"
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder={t('storyBiblePage.aiPlaceholder')}
            rows={5}
            autoFocus
          />
          <div className="sb-create-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="sb-btn-primary" disabled={!freeText.trim() || saving}>
              {saving ? (
                <><span className="sb-gen-spinner" /> {t('storyBiblePage.organizing')}</>
              ) : (
                t('storyBiblePage.createWithAi')
              )}
            </button>
          </div>
        </form>
      ) : (
        /* ── Manual mode: structured fields ── */
        <form onSubmit={handleManualSubmit}>
          <p className="sb-create-hint">{t('storyBiblePage.manualHint')}</p>
          <div className="sb-create-fields">
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.filter((c) => c.key !== 'all').map((c) => (
                <option key={c.key} value={c.key}>{c.icon} {c.label}</option>
              ))}
            </select>
            <input placeholder={t('storyBiblePage.fields.name')} value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
          </div>
          <textarea placeholder={t('storyBiblePage.descriptionOptional')} value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          <div className="sb-create-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="sb-btn-primary" disabled={!name.trim() || saving}>
              {saving ? t('common.creating') : t('storyBiblePage.createEntry')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ═══════ Legacy Story Bible View (read-only fallback) ═══════ */

function LegacyView({ viewModel }) {
  const { t } = useTranslation();
  const sectionLabels = useMemo(() => getLegacySectionLabels(t), [t]);
  if (!viewModel) return null;

  const sections = viewModel.sections || [];

  return (
    <div className="sb-legacy">
      {viewModel.summary && (
        <div className="sb-legacy-summary">
          <p>{viewModel.summary}</p>
        </div>
      )}
      {sections.map((section) => (
        <article key={section.key} className="sb-legacy-section">
          <h2>{sectionLabels[section.key] || section.title}</h2>
          {section.paragraphs?.map((p, i) => <p key={`p-${i}`}>{p}</p>)}
          {section.bullets?.length > 0 && (
            <ul>{section.bullets.map((b, i) => <li key={`b-${i}`}>{b}</li>)}</ul>
          )}
          {section.entries?.map((entry, i) => (
            <div key={`e-${i}`} className="sb-legacy-entry">
              <h3>{entry.title}</h3>
              {entry.paragraphs?.map((p, j) => <p key={`ep-${j}`}>{p}</p>)}
              {entry.bullets?.length > 0 && (
                <ul>{entry.bullets.map((b, j) => <li key={`eb-${j}`}>{b}</li>)}</ul>
              )}
            </div>
          ))}
        </article>
      ))}
    </div>
  );
}

export default StoryBiblePage;
